import { Box, Card, CardContent, Divider, Icon, Typography } from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Button } from '../../../shared/components';
import { feedback } from '../../../shared/services';
import { IConnectResult, whatsAppService } from '../../../shared/services/api/whatsapp';
import { IPropsBusinessPage } from '../Business';

export const WhatsAppBotConnect: React.FC<IPropsBusinessPage> = ({ business }) => {
  const [qrCode, setQrCode] = useState<string | null>(null);

  const { data: status, refetch: refetchStatus } = useQuery({
    queryKey: ['whatsapp-status', business.id],
    queryFn: () => whatsAppService.getConnectionStatus(business.id),
    refetchInterval: (data) => (data?.connected ? false : 5000), // polling enquanto não conectar
  });

  const { mutate: connect, isLoading: isConnecting } = useMutation<IConnectResult>(
    () => whatsAppService.connectInstance(business.id),
    {
      onSuccess: (data) => {
        if (data.qrCodeBase64) {
          setQrCode(data.qrCodeBase64);
        } else {
          feedback('QR Code gerado, mas sem imagem para exibir', 'warning');
        }
      },
      onError: (err: unknown) => feedback(String(err), 'error'),
    }
  );

  const { mutate: disconnect, isLoading: isDisconnecting } = useMutation(
    () => whatsAppService.disconnectInstance(business.id),
    {
      onSuccess: () => {
        setQrCode(null);
        refetchStatus();
        feedback('WhatsApp desconectado', 'success');
      },
      onError: (err: unknown) => feedback(String(err), 'error'),
    }
  );

  // Auto-polling stops when connected
  useEffect(() => {
    if (status?.connected) {
      setQrCode(null);
    }
  }, [status?.connected]);

  const isConnected = status?.connected ?? false;

  return (
    <Card>
      <CardContent>
        <Typography variant='h6' gutterBottom>Conexão WhatsApp</Typography>
        <Divider sx={{ mb: 2 }} />

        {/* Status */}
        <Box display='flex' alignItems='center' gap={1} mb={2}>
          <Box
            sx={{
              width: 12,
              height: 12,
              borderRadius: '50%',
              bgcolor: isConnected ? 'success.main' : 'text.disabled',
            }}
          />
          <Typography>
            {isConnected
              ? `Conectado${status?.instanceName ? ` (${status.instanceName})` : ''}`
              : 'Desconectado'}
          </Typography>
        </Box>

        {/* QR Code */}
        {qrCode && (
          <Box display='flex' flexDirection='column' alignItems='center' mb={2}>
            <Typography variant='body2' color='text.secondary' mb={1}>
              Escaneie o QR Code com o WhatsApp do seu negócio
            </Typography>
            <Box
              component='img'
              src={qrCode.startsWith('data:') ? qrCode : `data:image/png;base64,${qrCode}`}
              alt='QR Code WhatsApp'
              sx={{
                width: 250,
                height: 250,
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
              }}
            />
          </Box>
        )}

        {/* Actions */}
        <Box display='flex' gap={2}>
          {!isConnected && (
            <Button
              variant='contained'
              onClick={() => connect()}
              isLoading={isConnecting}
              startIcon={<Icon>qr_code_scanner</Icon>}
            >
              {qrCode ? 'Gerar Novo QR Code' : 'Conectar WhatsApp'}
            </Button>
          )}

          {isConnected && (
            <Button
              variant='outlined'
              color='error'
              onClick={() => disconnect()}
              isLoading={isDisconnecting}
              startIcon={<Icon>link_off</Icon>}
            >
              Desconectar
            </Button>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};
